import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import ShareTeachDialog from "@/components/ShareTeachDialog";
import {
  Loader2, GraduationCap, ScrollText, NotebookPen, HandHeart, MessagesSquare,
  Sparkles, Trash2, FileText, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

const FORMAT_META = {
  sermon_outline: { label: "Sermon Outline", Icon: ScrollText },
  teaching_notes: { label: "Teaching / Small Group Notes", Icon: NotebookPen },
  devotional: { label: "Devotional", Icon: HandHeart },
  discussion_guide: { label: "Discussion Guide", Icon: MessagesSquare },
  nugget: { label: "Short Teaching Nugget", Icon: Sparkles },
};

function DraftCard({ item, onDelete }) {
  const navigate = useNavigate();
  const meta = FORMAT_META[item.format] || { label: "Teaching draft", Icon: GraduationCap };
  const Icon = meta.Icon;
  const pdfHref = `${BACKEND}/api/study/sessions/${item.session_id}/teach-share/pdf`;

  const del = async () => {
    if (!window.confirm("Delete this teaching draft? This can't be undone.")) return;
    try {
      await api.delete(`/study/sessions/${item.session_id}/teach-share`);
      toast.success("Draft removed");
      onDelete(item);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-3" data-testid="teach-draft-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#00E5FF]">
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {meta.label}
          </div>
          <h3 className="text-lg text-white mt-1">{item.title}</h3>
          {item.passage_reference && <p className="text-xs text-slate-500 mt-0.5">{item.passage_reference}</p>}
        </div>
        <span className="text-xs text-slate-500 shrink-0">{fmtDate(item.at)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          data-testid="teach-draft-open"
          onClick={() => navigate(`/study/${item.session_id}`)}
          className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.7} /> Open &amp; edit
        </button>
        <a
          data-testid="teach-draft-pdf"
          href={pdfHref}
          target="_blank"
          rel="noreferrer"
          className="h-9 px-3.5 rounded-full border border-[#29344D] text-slate-400 text-sm font-medium inline-flex items-center gap-1.5 hover:border-[#00E5FF]/50 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={1.7} /> PDF
        </a>
        <ShareTeachDialog sessionId={item.session_id} />
        <button
          data-testid="teach-draft-delete"
          onClick={del}
          className="ml-auto flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
        </button>
      </div>
    </div>
  );
}

export default function TeachShareGallery() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [enabled, setEnabled] = useState(null); // null = checking the feature flag

  // Single kill-switch: Teach & Share is hidden entirely (mobile AND desktop) unless the
  // teach_share flag is on. When on, it's a normal feature available on all devices.
  useEffect(() => {
    api.get("/me/subscription")
      .then(({ data }) => setEnabled(!!(data.features?.teach_share?.enabled)))
      .catch(() => setEnabled(false));
  }, []);

  const load = () => {
    setError(false);
    setItems(null);
    api.get("/me/teach-share")
      .then(({ data }) => setItems(data.drafts || []))
      .catch(() => { setError(true); setItems([]); });
  };

  useEffect(() => { if (enabled) load(); }, [enabled]);

  const removeItem = (it) => setItems((prev) => prev.filter((x) => x.session_id !== it.session_id));

  if (enabled === null) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} /></div>;
  }
  if (enabled === false) {
    return <Navigate to="/study" replace />;
  }

  return (
    <div className="px-6 py-10 md:max-w-2xl md:mx-auto md:py-14 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF]">
          <GraduationCap className="w-4 h-4" strokeWidth={1.5} /> Teach &amp; Share
        </div>
        <h1 className="text-3xl text-white">Your teaching drafts</h1>
        <p className="text-sm text-slate-400 leading-relaxed">
          Every resource you&rsquo;ve shaped from your own study and reflection &mdash; sermon outlines,
          teaching notes, devotionals and more &mdash; gathered to revisit, edit, export as PDF, or share.
          Drafts are built with you, never a substitute for the Word.
        </p>
      </header>

      {!items ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} /></div>
      ) : error ? (
        <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-10 text-center space-y-3" data-testid="teach-share-error">
          <p className="text-slate-400">We couldn&rsquo;t load your teaching drafts just now.</p>
          <button
            data-testid="teach-share-retry"
            onClick={load}
            className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium transition-colors"
          >
            Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-10 text-center space-y-2" data-testid="teach-share-empty">
          <GraduationCap className="w-8 h-8 mx-auto text-slate-500" strokeWidth={1.5} />
          <p className="text-slate-400">No teaching drafts yet.</p>
          <p className="text-sm text-slate-500">
            Finish a study and go a little deeper (3+ exchanges), then open Teach &amp; Share to shape your first draft &mdash; it&rsquo;ll gather here.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="teach-share-list">
          {items.map((it) => <DraftCard key={it.session_id} item={it} onDelete={removeItem} />)}
        </div>
      )}
    </div>
  );
}
